"use client";

import { ExperienceRoot } from "@contentful/experiences-sdk-react";
import React, { useMemo } from "react";
import "@/studio-config";
import { DataContextProvider } from "@/app/contextProviders/data-context";
import { useAppContext } from "@/app/contextProviders/app-context";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { replacePlaceholders, type PlaceholderOffice } from "@/lib/utils/replacePlaceholders";
import type { AppointmentInfo } from "@/lib/utils/appointment/appointment-info";
import SmileInvocaTelAutoTag from "@/app/components/SmileInvocaTelAutoTag/SmileInvocaTelAutoTag";

const queryClient = new QueryClient();

interface ExperienceProps {
	/**
	 * The page experience, serialized ONCE, with its {{office*}} placeholders
	 * still in place. Until 2026-09-10 the server also passed a second copy with
	 * the placeholders already replaced; both strings rode the RSC payload, so
	 * every /smile document carried the experience JSON twice (468 KB x 2 on
	 * production /smile/, 559 KB x 2 on staging - the largest single item in
	 * a 2.0-2.6 MB HTML document). The replaced copy is derived here instead,
	 * on the server and again on the client, from this string and serverOffice.
	 */
	experienceJSON: string | null;
	/**
	 * The office the server resolved for this request (cookie, then edge geo),
	 * projected onto the fields the placeholder map reads (pickPlaceholderOffice).
	 * The first render on both sides replaces placeholders from it, so the
	 * server HTML and the hydrating client compute the same string from the same
	 * inputs; a visitor picking a different office re-derives from that office.
	 */
	serverOffice: PlaceholderOffice | null;
	locale: string;
	externalData: Record<string, any>;
	appointmentInfo?: AppointmentInfo;
	preview: boolean;
}

const Experience: React.FC<ExperienceProps> = ({
	experienceJSON,
	serverOffice,
	locale,
	externalData,
	appointmentInfo,
	preview,
}) => {
	const { selectedOffice } = useAppContext();

	const selectedOfficeCode = selectedOffice?.code ?? null;
	const providerData = externalData.provider?.data ?? null;
	// Resolved per request by ExperienceWrapper (resolveEnvBaseUrl), so the
	// server render and the hydrating client agree on every {{base_path}} link.
	const envBaseUrl: string = externalData.envBaseUrl ?? "";

	const finalExperienceJSON = useMemo(() => {
		if (!experienceJSON) {
			return null;
		}

		// The server's office wins until the visitor selects a DIFFERENT one:
		// the same rule as before the single-copy change, when the pre-replaced
		// server string was returned for a null or matching selection.
		const office =
			selectedOffice && selectedOfficeCode !== (serverOffice?.code ?? null)
				? selectedOffice
				: serverOffice;

		return replacePlaceholders(experienceJSON, office, envBaseUrl, appointmentInfo, providerData);
	}, [
		experienceJSON,
		selectedOffice,
		selectedOfficeCode,
		serverOffice,
		envBaseUrl,
		appointmentInfo,
		providerData,
	]);

	return (
		<QueryClientProvider client={queryClient}>
			<DataContextProvider data={externalData} preview={preview}>
				<ExperienceRoot experience={finalExperienceJSON} locale={locale} debug={false} />
				{/* Invoca DNI safety net (matrix rows I-1/I-2/D6): tags any authored
				    tel: container-link with InvocaNumber after hydration and re-runs
				    PNAPI so the pool-number swap covers Contentful CTAs on every
				    /smile and /composable/smile Experience page. This mount only
				    covers routes rendered through Experience; slot-rendered chrome
				    (header/footer slots and the /smile/schedule dispatcher, which
				    render via SlotExperience) is covered by the sibling mount in
				    SlotExperience.tsx. Inert off /smile and inside the studio
				    editor. */}
				<SmileInvocaTelAutoTag />
			</DataContextProvider>
		</QueryClientProvider>
	);
};

export default Experience;
